import { useNavigate } from "react-router-dom";
import { AlertTriangle, MessageSquare, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MessengerIcon } from "@/components/icons/MessengerIcon";
import {
  useIntegrationReconnectStatus,
  type ReconnectProvider,
} from "@/hooks/useIntegrationReconnectStatus";

const PROVIDER_LABEL: Record<ReconnectProvider, string> = {
  whatsapp_evolution: "WhatsApp",
  whatsapp_uazapi: "WhatsApp Business",
  instagram: "Instagram",
  messenger: "Messenger",
};

function ProviderIcon({ provider }: { provider: ReconnectProvider }) {
  if (provider === "whatsapp_evolution" || provider === "whatsapp_uazapi") {
    return <MessageSquare className="h-4 w-4 text-green-600 dark:text-green-400" />;
  }
  if (provider === "instagram") {
    return <Instagram className="h-4 w-4 text-pink-500" />;
  }
  return <MessengerIcon className="text-blue-500" size={16} />;
}

export function IntegrationReconnectBadge() {
  const navigate = useNavigate();
  const { issues, isChecking } = useIntegrationReconnectStatus();

  if (isChecking || issues.length === 0) return null;

  const providers = Array.from(new Set(issues.map((i) => i.provider))) as ReconnectProvider[];
  const single = providers.length === 1 ? providers[0] : null;

  const labelText = single
    ? `Reconectar ${PROVIDER_LABEL[single]}${issues.length > 1 ? ` (${issues.length})` : ""}`
    : `Reconectar conexões (${issues.length})`;

  const tooltip = issues
    .map(
      (i) =>
        `${PROVIDER_LABEL[i.provider]} — ${i.label} (${
          i.status === "token_expired"
            ? "token expirado"
            : i.status === "error"
              ? "erro"
              : "desconectado"
        })`,
    )
    .join("\n");

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => navigate("/integrations")}
      title={tooltip}
      className="h-8 px-3 gap-2 bg-yellow-100 dark:bg-yellow-900/30 border-yellow-300 dark:border-yellow-700 text-yellow-800 dark:text-yellow-200 hover:bg-yellow-200 dark:hover:bg-yellow-900/50"
    >
      {single ? <ProviderIcon provider={single} /> : <AlertTriangle className="h-4 w-4" />}
      <AlertTriangle className="h-3 w-3 text-yellow-600 dark:text-yellow-400" />
      <span className="hidden sm:inline text-sm font-medium">{labelText}</span>
    </Button>
  );
}
